/**
 * What a KIND of part is.
 *
 * A part kind is a definition object, and nothing outside `kinds/` switches on
 * a kind string: the solver, the drawing, the joining rule and the diagnostics
 * all ask the definition. Adding a table is writing one file and registering
 * it — and if a part cannot be added that way, this interface is missing
 * something and THIS is what should change.
 */
import { AppliedForce, Body, Constraint, Part, Port, PortJoins, Problem, RopeContact, Vec2, World } from './types';

/** The few things a joining point takes, named once so a kind says which. */
export const JOINS = {
    /** A hook's eye, an axle, a table's corner: as many joins as you like. */
    any: { max: Infinity, takes: ['rope', 'rod', 'bond'] },
    /** One join of any kind: a weight's side, a balance's end. */
    one: { max: 1, takes: ['rope', 'rod', 'bond'] },
    /** One line and never a bolt: a rope's own end, a becket. */
    oneLine: { max: 1, takes: ['rope', 'rod'] },
    /** The one rope running over a rim. A rod is not threaded over a wheel. */
    oneRope: { max: 1, takes: ['rope'] },
    /** A rope's loose end: one line, and dropped on a point it ties on there. */
    looseEnd: { max: 1, takes: ['rope', 'rod'], snaps: 'rope' },
    /** A pulley's axle: any number of joins, and dropped on a point it bolts. */
    hub: { max: Infinity, takes: ['rope', 'rod', 'bond'], snaps: 'bond' },
} as const satisfies Record<string, PortJoins>;

export interface Freedom {
    /** The directions it may travel along. Empty means it cannot move. */
    axes: Vec2[];
    spins?: boolean;
    /**
     * This part runs on a TRACK, and the track decides for everything bolted
     * to it.
     *
     * A body is normally as free as its least free part, and where several
     * parts are free it takes the first answer going. That is fine while every
     * one of them means "it hangs" — but a sheave bolted to a runner travels
     * along the rail, not downwards, and which of the two answers won would
     * otherwise depend on the order the parts were placed in.
     */
    track?: boolean;
}

/**
 * What a part that other parts REST ON knows about its own face.
 *
 * A block on a table does not decide which ways it may travel — the table
 * does, and an incline decides differently. So the answer lives on the
 * surface, and `weight` never learns that tables exist.
 */
export interface Support {
    /**
     * Which ways something resting here may travel: ALONG the face first, then
     * INTO it.
     *
     * Two coordinates for what is plainly one degree of freedom, and that is
     * deliberate. The second one is held shut by a two-sided contact row, so
     * nothing actually moves along it — but its multiplier IS the normal
     * force, which means N is solved for and checked by `ΣF = ma` like every
     * other force on the bench, rather than being reconstructed afterwards
     * from the forces that were left over. Friction then has something real to
     * be a fraction of.
     */
    axes: Vec2[];
    /** Half the face's length, so a caller can keep a part on it. */
    half: number;
    /** Where a part sits when it is `s` metres along the face from the middle
     *  and its own body reaches `clear` metres out from the face. */
    seat(s: number, clear: number): Vec2;
    /** How far along the face a point is. The inverse of `seat`. */
    along(at: Vec2): number;
}

export interface PartKind<D = Record<string, unknown>> {
    key: string;
    /** For the parts rail. */
    label: string;

    /** A fresh one, dropped at a point. */
    create(at: Vec2, id: string): Part<D>;

    /** Its joining points, in bench coordinates. */
    ports(part: Part<D>, w: World): Port[];

    mass(part: Part<D>): number;
    /** About its own centre. Zero unless the part spins. */
    inertia?(part: Part<D>): number;

    /**
     * How much room it takes up around `at`, per axis.
     *
     * One concept, four users: has it reached the floor, may a rope pull it
     * into the part above it, how big to draw it, and does a click land on it.
     * Answering that in four places with four numbers is how a weight came to
     * stop half inside the floor.
     */
    extent(part: Part<D>): Vec2;

    freedom(part: Part<D>, w: World): Freedom;

    /** May a BOND be made to it? A rope end says no — it exists only to end a
     *  rope — and that one flag is why the joining table has three rows. */
    bondable: boolean;

    /** The one or two things about it worth reading at a glance, beside its
     *  name: a block's mass, a pulley's size. Empty or absent: just the name. */
    keyFacts?(part: Part<D>, w: World): string[];

    /**
     * Bolted to a surface — a part with `supports` — what this part becomes
     * there: data to set, and whether the bolt closes flush. A pulley fastened
     * to a table or a wedge is the small one, its centre on the point, so a rope
     * over it runs parallel to the face. Absent: a bolt leaves it as it was.
     */
    onFace?(part: Part<D>): { data: Partial<D>; flush: boolean };

    /** It stands on the floor and stays on it. */
    onFloor?: boolean;

    /**
     * How hard the floor grips it while it stands there: a coefficient of
     * friction, or `'fixed'` for not sliding at all. Absent means smooth.
     *
     * The PART's, not the floor's. Friction belongs to a pair of surfaces, and
     * a bench has one floor under many parts — a block of rubber and a block
     * of ice beside each other are two answers, and one number on the floor
     * could only give one.
     *
     * `'fixed'` is not a big number. A coefficient caps friction in proportion
     * to the normal force, and a part that has lifted off has none — so any
     * finite μ, however large, still lets go when it should, and ∞ · 0 is not
     * a cap at all. Fixed means "holds whenever it is touching".
     */
    floorGrip?(part: Part<D>): number | 'fixed';

    /** Bolted to the beam: it slides along the ceiling and never leaves it.
     *  Not the same as being unable to move — a table cannot move either, and
     *  it lives wherever it is put. */
    onBeam?: boolean;

    /** Where one goes when it is dropped on the bench: metres below the beam.
     *  Here rather than in the surface because it is a fact about the part —
     *  a hook belongs on the ceiling and a weight belongs near the floor. */
    home: number;

    /** What a part resting on one of this part's faces may do. Only a surface
     *  answers. `face` picks between them where there is a choice. */
    supports?(part: Part<D>, w: World, face?: string): Support | undefined;

    /** The faces something can be stood on, when there is more than one.
     *  Absent means there is exactly one and it needs no name. */
    faces?(part: Part<D>, w: World): { id: string; label: string }[];

    /**
     * Fields of `data` that are WORKED OUT rather than set.
     *
     * A wedge's height is where its two sides meet, so it follows from the
     * base and the angles and there is nothing for a reader to choose. Offering
     * a slider for it is offering a control that cannot move — and one that
     * lies about which numbers this shape is made of.
     */
    derived?: string[];

    /** Corners a reader can drag to resize it, in bench coordinates. */
    handles?(part: Part<D>, w: World): { id: string; at: Vec2 }[];
    /** Its shape with one handle dragged to `to`: the data that changes and, if
     *  its base moved, where it now stands. */
    resize?(part: Part<D>, handle: string, to: Vec2, w: World): { data: Partial<D>; at?: Vec2 };

    /** The closed outline of its body, in bench coordinates — for drawing it
     *  and for ringing it when selected. A circle round a two-metre wedge
     *  marks nothing; its own shape marks it exactly. */
    outline?(part: Part<D>, w: World): Vec2[];
    /** A join point ANYWHERE along its top, at this x — for a kind that is
     *  something to be fastened to rather than only at fixed corners. Returns
     *  the part with the point added (or the same part, if one is already
     *  there) and that point's port id. */
    mountAt?(part: Part<D>, x: number): { part: Part<D>; portId: string };
    /** The same part without any join points that nothing uses — `used` is
     *  every port id of this part some link still names. For a kind whose join
     *  points the reader MADE, so they go when their last join does. */
    tidy?(part: Part<D>, used: Set<string>): Part<D>;

    /**
     * Can a rope running over this part carry a DIFFERENT tension on each
     * side of it?
     *
     * The question every "one rope, one tension" answer quietly assumes away.
     * It is false for the ideal sheave a textbook starts with — massless, and
     * turning freely, so there is nothing for a difference to act against —
     * and true the moment the wheel has inertia to spin up or a bearing that
     * drags. Saying it here makes the assumption a property of the PART, where
     * a reader can see it and change it, instead of a fact about ropes.
     */
    gripsRope?(part: Part<D>, w: World): boolean;

    /** Where a rope touches it on its way to `toward`, and the arc if it wraps. */
    ropeContact(part: Part<D>, port: Port, toward: Vec2): RopeContact;

    /** Forces it applies directly, named, so they appear on a free-body diagram
     *  as themselves rather than as an anonymous number. Gravity is generic and
     *  is not listed here. */
    forces?(part: Part<D>, w: World): AppliedForce[];

    /**
     * Rows it contributes on its own — a surface's contact plane, and the
     * friction on it. Most kinds contribute none: the generic rules in
     * `constraints.ts` cover them.
     *
     * The bodies come in because a row names COORDINATES, and which coordinate
     * is which is a fact about the body a part has ended up in, not about the
     * part.
     */
    constraints?(part: Part<D>, w: World, bodies: Body[]): Constraint[];

    /** Its letter, for when the bench has to number them: M for a weight, P
     *  for a pulley. One or two characters — it is read beside a subscript. */
    tag: string;

    /**
     * Where its name goes, or `null` if it draws its own.
     *
     * Required, and here rather than in the drawing, because WHERE a part has
     * clear space is a fact about that part's shape and nothing else knows it:
     * a hook has the beam above and its rope below, a sheave has ropes at both
     * rims, a wedge has blocks standing on every face. Guessed on a part's
     * behalf, the name does not go missing — it gets written ACROSS A ROPE,
     * which is worse, and which you only find out by looking.
     *
     * Required rather than optional so that a new kind cannot quietly fall
     * into somebody else's default. Answering `null` is a real answer: it
     * means the name is part of the glyph, the way a weight carries its own
     * label inside the block.
     */
    nameAt(part: Part<D>, w: World): { at: Vec2; anchor: 'start' | 'middle' | 'end' } | null;

    /** What it is called, in a fault and in the readouts. Nearly always
     *  `numbered(part, w, tag)`; a kind overrides it only when it has
     *  something better to say. */
    name(part: Part<D>, w: World): string;

    /**
     * What ONE of its join points is called — the suffix after the part's name.
     *
     * A pulley has three and which one a rope is on is a fact about the
     * arrangement, not a detail: a rope threaded over the left rim stays on the
     * left however the bench is dragged about. That is true in the model and
     * has never not been true, but it was impossible to READ, so a rope drawn
     * doubling back over a wheel looked exactly like a rope that had flipped
     * sides, and the only way to tell was to go and check. Naming them ends the
     * question: `P₂_L`, `P₂_R`, `P₂_C`.
     *
     * Absent means the port id is already clear enough — a hook is a hook.
     */
    portName?(port: Port): string | null;

    /** What is wrong with it, said in physics. */
    diagnose?(part: Part<D>, w: World): Problem[];
}

const SUBSCRIPT = '₁₂₃₄₅₆₇₈₉';

/**
 * What a part is called when nobody has named it: its kind's letter and its
 * number on the bench — M₁, P₂, H₁.
 *
 * A part that cannot be referred to cannot be talked about — not in the
 * readouts, not in a fault, and not out loud in a classroom. Three rows
 * reading "the ceiling hook" are three different hooks and a reader has no way
 * to tell which is which, so every kind numbers its own, and an authored label
 * always wins.
 *
 * Here rather than in each kind because it was already copied once and would
 * have been copied five more times; each kind supplies only its letter.
 */
export function numbered(part: { id: string; kind: string; label?: string }, w: World, tag: string): string {
    if (part.label) return part.label;
    const same = w.parts.filter(p => p.kind === part.kind);
    if (!same.some(p => p.id === part.id)) return tag;
    /* Numbered in bench order, but never onto a name already in use: a preset
       names its own parts (m₁, m₂), and with m₁ deleted the next unnamed
       block was second of its kind — m₂ — beside the preset's own m₂. */
    const name = (n: number): string => `${tag}${SUBSCRIPT[n - 1] ?? n}`;
    const used = new Set(same.map(p => p.label).filter((l): l is string => !!l));
    for (const p of same) {
        if (p.label) continue;
        let n = same.indexOf(p) + 1;
        while (used.has(name(n))) n++;
        if (p.id === part.id) return name(n);
        used.add(name(n));
    }
    return tag;
}

const REGISTRY = new Map<string, PartKind<never>>();

export function registerKind<D>(kind: PartKind<D>): void {
    REGISTRY.set(kind.key, kind as unknown as PartKind<never>);
}

export function kindOf(key: string): PartKind<never> {
    const k = REGISTRY.get(key);
    if (!k) throw new Error(`mechanics: no part kind "${key}" is registered`);
    return k;
}

export const allKinds = (): PartKind<never>[] => [...REGISTRY.values()];

/** The definition for a part. Sugar, because every caller wants this one. */
export const defOf = (part: Part): PartKind<never> => kindOf(part.kind);
